/**
 * PRP-WEB-1 W2.5: the project page's Overview tab (the default). One card per estimate from
 * GET /projects/{id}/overview (newest first) with Open → the line items in one click; beside it
 * the plan thumbnail with "Open floor plan" (→ /project/:id/plan, W3's route); under them the
 * claim facts and the chat estimate's open questions (ClaimFactsSection carries OpenItemsBanner).
 *
 * If the overview cannot be read (older server) the cards fall back to the project's imports
 * list, without totals or counts. Deleting an estimate asks in-app, hides the card, and sends the
 * DELETE only after the Undo window (W2.8, useUndoableDelete).
 *
 * The delete state (pending send, hidden cards, the toast, a failure message) belongs to the PAGE
 * (`useEstimateDeletes`, passed in as `deletes`): this tab unmounts on a tab switch, and that must
 * neither send the DELETE nor lose the toast or the hidden card.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BackendAPI } from '../../lib/api';
import { ProjectOverviewAPI } from '../../lib/projectOverviewApi';
import type { OverviewEstimate, ProjectOverview as Overview } from '../../lib/projectOverviewApi';
import type { FieldNote, ImportSummary } from '../../types';
import { chatEstimateName, estimatePageUrl, importName } from './ChatEstimatesCard';
import ClaimFactsSection from './ClaimFactsSection';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import EstimateCard from './EstimateCard';
import { FloorPlanView } from './FloorPlanView';
import { useUndoableDelete, type PendingDelete } from './UndoDeleteToast';
import { Banner } from './ui/banner';
import { Button } from './ui/button';
import { Card } from './ui/card';

/** The project page's estimate deletes: one Undo queue and its state, owned by the page. */
export interface EstimateDeletes {
  schedule: (_d: PendingDelete) => void;
  /** Send the pending delete now (the page is being left). */
  flush: () => void;
  /** The toast to render at page level (null when nothing is pending). */
  toast: JSX.Element | null;
  /** Cards hidden while their delete is pending or after it went. */
  hidden: Set<string>;
  setHidden: (_parseHash: string, _on: boolean) => void;
  failed: string | null;
  setFailed: (_msg: string | null) => void;
}

/** Call once in the project page; pass the result to {@link ProjectOverview} as `deletes`. */
export function useEstimateDeletes(): EstimateDeletes {
  const { schedule, flush, toast } = useUndoableDelete();
  const [hidden, setHiddenSet] = useState<Set<string>>(() => new Set());
  const [failed, setFailed] = useState<string | null>(null);
  const setHidden = useCallback(
    (hash: string, on: boolean) =>
      setHiddenSet((prev) => {
        const next = new Set(prev);
        if (on) next.add(hash);
        else next.delete(hash);
        return next;
      }),
    [],
  );
  return { schedule, flush, toast, hidden, setHidden, failed, setFailed };
}

export interface ProjectOverviewProps {
  projectId: string;
  projectName: string;
  notes: FieldNote[];
  /** The project's imports (`listImports(projectId)`), the fallback when the overview is unreadable. */
  imports: ImportSummary[];
  chatEstimateId?: string | null;
  onOpen: (_url: string) => void;
  /** An estimate was deleted for good (after the Undo window). */
  onDeleted?: (_parseHash: string) => void;
  /** The page's delete queue (`useEstimateDeletes`); the page renders its toast. */
  deletes: EstimateDeletes;
}

/** The chat prompt that starts an estimate for this project. */
export function startInChatPrompt(projectName: string): string {
  return `Start an estimate for my AIEA project ${projectName}: call start_estimate_from_project.`;
}

/** Cards built from the imports list when the overview is not available. */
export function cardsFromImports(imports: ImportSummary[], projectId: string): OverviewEstimate[] {
  return [...imports]
    .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))
    .map((r) => {
      const chat = r.parse_hash.startsWith('mcp-');
      return {
        parse_hash: r.parse_hash,
        name: chat ? chatEstimateName(r) : importName(r),
        source: chat ? 'chat' : 'import',
        follows_plan: false,
        total: null,
        rooms: 0,
        line_items: 0,
        last_changed_at: r.created_at ?? null,
        open_url: estimatePageUrl({ ...r, project_id: r.project_id ?? projectId }),
      };
    });
}

export default function ProjectOverview({
  projectId,
  projectName,
  notes,
  imports,
  chatEstimateId,
  onOpen,
  onDeleted,
  deletes,
}: ProjectOverviewProps) {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'fallback'>('loading');
  const [asking, setAsking] = useState<OverviewEstimate | null>(null);
  const [copied, setCopied] = useState<'idle' | 'copied' | 'failed'>('idle');
  const { schedule, hidden, setHidden: setHiddenFor, failed, setFailed } = deletes;

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    ProjectOverviewAPI.getOverview(projectId)
      .then((o) => {
        if (cancelled) return;
        setOverview(o);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('fallback');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // The overview's cards when it has any; otherwise the imports list (older server, or a body
  // with no estimates while the project plainly has imports).
  const fromOverview = state === 'ready' && (overview?.estimates.length ?? 0) > 0;
  const cards = useMemo(
    () => (fromOverview ? overview!.estimates : cardsFromImports(imports, projectId)).filter((e) => !hidden.has(e.parse_hash)),
    [fromOverview, overview, imports, projectId, hidden],
  );
  const hasPlan = notes.some((n) => n.room_geometry);

  const confirmDelete = (e: OverviewEstimate) => {
    setAsking(null);
    setFailed(null);
    setHiddenFor(e.parse_hash, true);
    schedule({
      id: e.parse_hash,
      label: e.name,
      send: () => BackendAPI.deleteImport(e.parse_hash).then(() => onDeleted?.(e.parse_hash)),
      onUndo: () => setHiddenFor(e.parse_hash, false),
      onFailed: () => {
        setHiddenFor(e.parse_hash, false);
        setFailed(`Could not delete ${e.name}. It is still here.`);
      },
    });
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(startInChatPrompt(projectName));
      setCopied('copied');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto" role="tabpanel" id="page-tab-overview" aria-labelledby="page-tab-btn-overview">
      <div className="mx-auto w-full max-w-5xl px-4 py-4" data-testid="project-overview">
        {failed && (
          <Banner tone="warn" className="mb-3">
            {failed}
          </Banner>
        )}
        <div className={hasPlan ? 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]' : ''}>
          <section aria-labelledby="overview-estimates" className="min-w-0">
            <h2 id="overview-estimates" className="mb-2 text-[13px] font-[560] uppercase tracking-wide text-[var(--tx-lo)]">
              Estimates
            </h2>
            {state === 'loading' && imports.length === 0 ? (
              <p className="text-[13px] text-[var(--tx-lo)]" role="status">
                Loading estimates…
              </p>
            ) : cards.length === 0 ? (
              <Card className="p-4" data-testid="overview-no-estimate">
                <p className="m-0 text-[13px] text-[var(--tx-mid)]">No estimate yet — start one in chat.</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={() => void copyPrompt()} data-testid="overview-copy-prompt">
                    {copied === 'copied' ? 'Copied' : 'Copy chat prompt'}
                  </Button>
                  {copied === 'failed' && (
                    <span className="select-all break-words text-[12px] text-[var(--tx-lo)]">
                      {startInChatPrompt(projectName)}
                    </span>
                  )}
                </div>
              </Card>
            ) : (
              <div className="flex flex-col gap-2">
                {cards.map((e) => (
                  <EstimateCard
                    key={e.parse_hash}
                    estimate={e}
                    partial={!fromOverview}
                    onOpen={() => onOpen(e.open_url)}
                    onDelete={() => setAsking(e)}
                  />
                ))}
              </div>
            )}
          </section>
          {hasPlan && (
            <Card className="min-w-0 self-start p-3" data-testid="overview-plan">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h2 className="text-[13px] font-[560] uppercase tracking-wide text-[var(--tx-lo)]">Floor plan</h2>
                <Button asChild variant="secondary" size="sm">
                  <Link to={`/project/${encodeURIComponent(projectId)}/plan`} data-testid="open-floor-plan">
                    Open floor plan
                  </Link>
                </Button>
              </div>
              <div className="max-h-[300px] overflow-hidden">
                <FloorPlanView notes={notes} />
              </div>
            </Card>
          )}
        </div>
        <ClaimFactsSection projectId={projectId} chatEstimateId={chatEstimateId} className="mt-4" />
      </div>
      {asking && (
        <ConfirmDeleteDialog title="Delete estimate?" onClose={() => setAsking(null)} onConfirm={() => confirmDelete(asking)}>
          <b className="text-[var(--tx-hi)]">{asking.name}</b> and its line items will be deleted.
        </ConfirmDeleteDialog>
      )}
    </div>
  );
}
